// src/components/ui/tremor-slider.tsx

/**
 * @fileoverview Tremor-styled slider component
 * 
 * A custom slider component that matches the Tremor design system
 * with proper stepper controls and value display.
 * 
 * Key UX Considerations:
 * - Input field allows temporary invalid values during typing to prevent interrupting user flow
 * - Lower bound enforcement is deferred until blur to allow typing multi-digit numbers (e.g., "22" when min is 20)
 * - Upper bound is enforced immediately to provide clear feedback when limit is exceeded
 * - Decimal precision is controlled by step size to prevent infinite decimal places
 * - State synchronization ensures slider and input stay in sync during all interactions
 * - Validation messages provide clear feedback when user input doesn't meet requirements
 * 
 * @module components/ui/tremor-slider
 * @exports {React.Component} TremorSlider - The main slider component
 * @exports {TypeScript.Interface} TremorSliderProps - Component props interface
 */

"use client"

import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { cn } from "~/lib/utils/index"

/**
 * Props interface for the TremorSlider component
 * 
 * @interface TremorSliderProps
 * @extends {Omit<React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>, 'value' | 'onValueChange'>}
 * @property {string} [className] - Additional CSS class name for styling
 * @property {boolean} [showValue=true] - Whether to show the current value input field
 * @property {boolean} [showSteppers=true] - Whether to show increment/decrement stepper buttons
 * @property {string} [valueLabel] - Optional label to display next to the value
 * @property {number[]} [value] - Current value of the slider as an array
 * @property {number} [min=0] - Minimum value allowed
 * @property {number} [max=100] - Maximum value allowed
 * @property {number} [step=1] - Step increment for value changes
 * @property {function} [onValueChange] - Callback fired when the value changes
 *   @property {number[]} onValueChange.value - The new value array
 * @property {boolean} [disabled=false] - Whether the slider is disabled
 * @property {boolean} [showValidation=true] - Whether to show validation messages
 */
interface TremorSliderProps extends Omit<React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>, 'value' | 'onValueChange'> {
  className?: string
  showValue?: boolean
  showSteppers?: boolean
  valueLabel?: string
  value?: number[]
  min?: number
  max?: number
  step?: number
  onValueChange?: (value: number[]) => void
  disabled?: boolean
  showValidation?: boolean
}

/**
 * Validation message types for user feedback
 */
type ValidationMessage = {
  type: 'error' | 'warning' | 'info'
  message: string
}

/**
 * Calculates the number of decimal places allowed based on step size
 * This prevents infinite decimal precision when step is an integer
 * 
 * @param {number} step - The step size
 * @returns {number} Maximum number of decimal places allowed
 */
const getMaxDecimalPlaces = (step: number): number => {
  if (step >= 1) return 0 // Integer steps don't allow decimals
  
  const stepStr = step.toString()
  const decimalIndex = stepStr.indexOf('.')
  
  if (decimalIndex === -1) return 0
  
  return stepStr.length - decimalIndex - 1
}


/**
 * Safely parses a string to a number with fallback
 * 
 * @param {string} value - The string value to parse
 * @param {number} fallback - The fallback value if parsing fails
 * @returns {number} The parsed number or fallback
 */
const safeParseNumber = (value: string, fallback: number): number => {
  if (value === '' || value === '-') return fallback
  
  const parsed = Number(value)
  return Number.isNaN(parsed) ? fallback : parsed
}

/**
 * Rounds a number to the precision defined by the step size
 * 
 * @param {number} value - The value to round
 * @param {number} step - The step size that defines precision
 * @returns {number} The rounded value
 */
const roundToStep = (value: number, step: number): number => {
  const decimalPlaces = getMaxDecimalPlaces(step)
  return Number(value.toFixed(decimalPlaces))
}

/**
 * Generates appropriate validation messages based on input and constraints
 * 
 * @param {string} input - The current input value
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @param {number} step - Step size
 * @param {number} maxDecimalPlaces - Maximum decimal places allowed
 * @returns {ValidationMessage | null} Validation message or null if valid
 */
const getValidationMessage = (
  input: string, 
  min: number, 
  max: number, 
  step: number, 
  maxDecimalPlaces: number
): ValidationMessage | null => {
  if (input === '' || input === '-') return null
  
  // Check for invalid characters
  const numericPattern = /^-?\d*\.?\d*$/
  if (!numericPattern.test(input)) {
    return {
      type: 'error',
      message: 'Only numbers are allowed'
    }
  }
  
  // Check decimal places
  const decimalIndex = input.indexOf('.')
  if (decimalIndex !== -1) {
    const decimalPlaces = input.length - decimalIndex - 1
    if (decimalPlaces > maxDecimalPlaces) {
      if (maxDecimalPlaces === 0) {
        return {
          type: 'error',
          message: 'Decimal values are not allowed'
        }
      } else {
        return {
          type: 'error',
          message: `Maximum ${maxDecimalPlaces} decimal place${maxDecimalPlaces === 1 ? '' : 's'} allowed`
        }
      }
    }
  }
  
  // Check if it's a valid number
  const numericValue = safeParseNumber(input, min)
  if (!Number.isFinite(numericValue)) {
    return {
      type: 'error',
      message: 'Please enter a valid number'
    }
  }
  
  // Check bounds
  if (numericValue > max) {
    return {
      type: 'error',
      message: `Maximum value is ${max}`
    }
  }
  
  if (numericValue < min) {
    return {
      type: 'warning',
      message: `Minimum value is ${min}`
    }
  }
  
  // Check step alignment (only show if value is significantly off)
  const remainder = (numericValue - min) % step
  if (remainder !== 0 && Math.abs(remainder) > 0.0001) {
    const nearestStep = Math.round((numericValue - min) / step) * step + min
    return {
      type: 'info',
      message: `Value will be rounded to ${nearestStep}`
    }
  }
  
  return null
}

/**
 * Tremor-styled slider with integrated steppers and value display
 * 
 * This component provides a comprehensive slider interface with:
 * - Visual slider track with thumb control
 * - Optional stepper buttons for precise value adjustment
 * - Optional numeric input field for direct value entry
 * - Full keyboard and screen reader accessibility
 * - Dark mode support with proper contrast ratios
 * - Smart decimal handling based on step size
 * - Contextual validation messages for better user experience
 * 
 * UX Design Philosophy:
 * The input field uses a "permissive typing, strict validation" approach:
 * - During typing: Only prevents invalid characters, enforces upper bounds, and limits decimal precision
 * - On blur: Applies full validation including lower bounds and step rounding
 * - This prevents interrupting users typing multi-digit numbers (e.g., "22" when min is 20)
 * - Decimal precision is controlled by step size to prevent infinite decimal places
 * - Validation messages provide clear, contextual feedback without being intrusive
 * 
 * @param {TremorSliderProps} props - Component props
 * @param {React.Ref} ref - Forwarded ref to the slider root element
 * @returns {React.ReactElement} The rendered slider component
 * 
 * @example
 * // Basic usage with integer steps (no decimals allowed)
 * <TremorSlider 
 *   value={[25]} 
 *   onValueChange={(value) => setValue(value)} 
 *   min={0} 
 *   max={100}
 *   step={1}
 * />
 * 
 * @example
 * // With decimal precision controlled by step
 * <TremorSlider 
 *   value={[fontSize]} 
 *   onValueChange={(value) => setFontSize(value[0])} 
 *   min={12} 
 *   max={72} 
 *   step={0.5} // Allows one decimal place
 *   valueLabel="px"
 *   className="w-64"
 * />
 */
const TremorSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  TremorSliderProps
>(({ 
  className, 
  showValue = true, 
  showSteppers = true, 
  valueLabel,
  value = [0],
  min = 0,
  max = 100,
  step = 1,
  onValueChange,
  disabled = false,
  showValidation = true,
  ...props 
}, ref): React.ReactElement => {
  // Calculate maximum decimal places based on step size
  const maxDecimalPlaces = React.useMemo(() => getMaxDecimalPlaces(step), [step])
  
  // Internal state to track current value, synced with external value prop
  const [currentValue, setCurrentValue] = React.useState<number>(() => value[0] ?? min)
  
  // Separate state for the input field to allow temporary invalid values during typing
  // This is crucial for good UX - allows typing "22" when min is 20 without interference
  const [inputValue, setInputValue] = React.useState<string>(() => String(value[0] ?? min))
  
  // Track if the user is currently editing the input field
  // This prevents external value changes from overriding user input during typing
  const [isEditing, setIsEditing] = React.useState<boolean>(false)
  
  // Validation message state for user feedback
  const [validationMessage, setValidationMessage] = React.useState<ValidationMessage | null>(null)
  
  // Timer for clearing validation messages
  const validationTimeoutRef = React.useRef<NodeJS.Timeout>(undefined)
  
  // Sync internal state with external value prop changes
  // Only updates input value when user is not actively editing to preserve typing flow
  React.useEffect(() => {
    const newValue = value[0] ?? min
    if (newValue !== currentValue) {
      setCurrentValue(newValue)
      // Critical: Only update input value if user is not currently editing
      // This prevents interrupting the user's typing flow
      if (!isEditing) {
        setInputValue(String(newValue))
        setValidationMessage(null) // Clear validation when value changes externally
      }
    }
  }, [value, min, currentValue, isEditing])

  /**
   * Shows a validation message with auto-clear timeout
   */
  const showValidationMessage = React.useCallback((message: ValidationMessage) => {
    if (!showValidation) return
    
    setValidationMessage(message)
    
    // Clear existing timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }
    
    // Auto-clear message after delay (longer for errors, shorter for info)
    const delay = message.type === 'error' ? 4000 : message.type === 'warning' ? 3000 : 2000
    validationTimeoutRef.current = setTimeout(() => {
      setValidationMessage(null)
    }, delay)
  }, [showValidation])

  /**
   * Clears validation message immediately
   */
  const clearValidationMessage = React.useCallback(() => {
    setValidationMessage(null)
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }
  }, [])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  /**
   * Constrains a value within the defined min/max bounds and rounds to step precision
   * 
   * @param {number} val - The value to constrain
   * @returns {number} The constrained and rounded value
   */
  const constrainValue = React.useCallback((val: number): number => {
    const bounded = Math.max(min, Math.min(max, val))
    return roundToStep(bounded, step)
  }, [min, max, step])

  /**
   * Handles value changes from slider and stepper interactions
   * Updates both internal state and input display, then calls external onChange
   * 
   * @param {number[]} newValue - The new value array
   */
  const handleValueChange = React.useCallback((newValue: number[]) => {
    const constrainedValue = constrainValue(newValue[0] ?? min)
    setCurrentValue(constrainedValue)
    // Always update input value for slider/stepper changes to keep UI in sync
    setInputValue(String(constrainedValue))
    clearValidationMessage() // Clear any existing validation messages
    onValueChange?.([constrainedValue])
  }, [constrainValue, min, onValueChange, clearValidationMessage])

  /**
   * Handles stepper button clicks for incrementing/decrementing value
   * 
   * @param {number} delta - The amount to change the value by (positive or negative)
   */
  const handleStepperChange = React.useCallback((delta: number) => {
    if (disabled) return
    
    const newValue = constrainValue(currentValue + delta)
    handleValueChange([newValue])
  }, [currentValue, constrainValue, handleValueChange, disabled])

  /**
   * Handles direct input changes with smart validation
   * 
   * Key UX Strategy:
   * - Allows typing partial numbers (e.g., "2" when typing "22")
   * - Only enforces upper bound during typing (prevents exceeding max)
   * - Defers lower bound enforcement until blur (prevents interrupting multi-digit entry)
   * - Controls decimal precision based on step size to prevent infinite decimals
   * - Provides immediate feedback for values that exceed maximum or have too many decimals
   * - Shows helpful validation messages for common input mistakes
   * 
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   */
  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    
    const inputValue = e.target.value
    
    // Check for validation issues and show appropriate messages
    const validation = getValidationMessage(inputValue, min, max, step, maxDecimalPlaces)
    
    // For certain validation errors, prevent the input change entirely
    if (validation?.type === 'error') {
      // Check if it's a character validation error or decimal precision error
      const numericPattern = /^-?\d*\.?\d*$/
      if (!numericPattern.test(inputValue)) {
        showValidationMessage(validation)
        return // Don't update input for invalid characters
      }
      
      // Check decimal places
      const decimalIndex = inputValue.indexOf('.')
      if (decimalIndex !== -1) {
        const decimalPlaces = inputValue.length - decimalIndex - 1
        if (decimalPlaces > maxDecimalPlaces) {
          showValidationMessage(validation)
          return // Don't update input for excessive decimal places
        }
      }
    }
    
    // Always update the input display to show what user typed (if it passed basic validation)
    setInputValue(inputValue)
    
    // Clear validation message if input becomes valid
    if (!validation) {
      clearValidationMessage()
    }
    
    // If it's a valid number, apply smart validation
    if (inputValue !== '' && inputValue !== '-') {
      const numericValue = safeParseNumber(inputValue, currentValue)
      
      if (Number.isFinite(numericValue)) {
        // CRITICAL UX DECISION: Only enforce upper bound during typing
        // This prevents the bad UX where typing "2" (when min is 20) gets forced to 20,
        // making it impossible to type "22" naturally
        if (numericValue <= max) {
          // Value is within acceptable range, update immediately
          // Round to step precision to maintain consistency
          const roundedValue = roundToStep(numericValue, step)
          setCurrentValue(roundedValue)
          onValueChange?.([roundedValue])
          
          // Show info message for step rounding if applicable
          if (validation?.type === 'info') {
            showValidationMessage(validation)
          }
        } else {
          // Value exceeds maximum - provide immediate feedback by constraining
          // This is acceptable because user has clearly exceeded the limit
          const constrainedValue = max
          setCurrentValue(constrainedValue)
          setInputValue(String(constrainedValue))
          onValueChange?.([constrainedValue])
          showValidationMessage({
            type: 'error',
            message: `Maximum value is ${max}`
          })
        }
      }
    } else if (validation?.type === 'warning') {
      // Show warning for values below minimum (but don't constrain during typing)
      showValidationMessage(validation)
    }
  }, [currentValue, max, min, step, maxDecimalPlaces, onValueChange, disabled, showValidationMessage, clearValidationMessage])

  /**
   * Handles input focus to mark editing state
   * This prevents external value changes from overriding user input
   */
  const handleInputFocus = React.useCallback(() => {
    setIsEditing(true)
    clearValidationMessage() // Clear validation messages when user starts editing
  }, [clearValidationMessage])

  /**
   * Handles input blur to finalize value and ensure full validation
   * 
   * This is where we apply the lower bound constraint that was deferred during typing.
   * By waiting until blur, we allow users to type multi-digit numbers naturally.
   * We also apply step rounding here to ensure the final value aligns with step increments.
   * 
   * Example: User wants to type "22" when min is 20
   * - During typing "2": No constraint applied (would be bad UX to force to 20)
   * - On blur with "2": Now we apply min constraint and set to 20
   * - During typing "22": No constraint needed, value is valid
   */
  const handleInputBlur = React.useCallback(() => {
    setIsEditing(false)
    
    // Parse the current input value
    const numericValue = safeParseNumber(inputValue, currentValue)
    
    // NOW we apply full validation including lower bound and step rounding
    // This was deferred during typing to prevent UX issues
    const constrainedValue = constrainValue(numericValue)
    
    // Check if value was adjusted and show appropriate message
    if (constrainedValue !== numericValue) {
      if (numericValue < min) {
        showValidationMessage({
          type: 'info',
          message: `Value adjusted to minimum: ${min}`
        })
      } else if (numericValue > max) {
        showValidationMessage({
          type: 'info',
          message: `Value adjusted to maximum: ${max}`
        })
      } else {
        // Step rounding occurred
        showValidationMessage({
          type: 'info',
          message: `Value rounded to nearest step: ${constrainedValue}`
        })
      }
    }
    
    // Update all states to the final constrained value
    setCurrentValue(constrainedValue)
    setInputValue(String(constrainedValue))
    onValueChange?.([constrainedValue])
  }, [inputValue, currentValue, constrainValue, onValueChange, min, max, showValidationMessage])

  /**
   * Handles Enter key press to blur the input and finalize value
   */
  const handleInputKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      // Reset to current value and clear validation
      setInputValue(String(currentValue))
      clearValidationMessage()
      e.currentTarget.blur()
    }
  }, [currentValue, clearValidationMessage])

  // Calculate boundary states for stepper button disabled states
  const isAtMin = currentValue <= min
  const isAtMax = currentValue >= max

  // Calculate slider progress percentage for visual feedback
  const progressPercentage = React.useMemo(() => {
    return ((currentValue - min) / (max - min)) * 100
  }, [currentValue, min, max])

   // Determine input field styling based on validation state
   const getInputClassName = React.useCallback(() => {
     const baseClasses = "w-20 px-3 py-1 text-sm text-center bg-transparent focus:outline-none focus:ring-0 border-0 transition-colors"
     
     if (!validationMessage) {
       return cn(baseClasses, "text-gray-900 dark:text-white")
     }
     
     switch (validationMessage.type) {
       case 'error':
         return cn(baseClasses, "text-red-700 dark:text-red-300")
       case 'warning':
         return cn(baseClasses, "text-amber-700 dark:text-amber-300")
       case 'info':
         return cn(baseClasses, "text-blue-700 dark:text-blue-300")
       default:
         return cn(baseClasses, "text-gray-900 dark:text-white")
     }
   }, [validationMessage])

   const getStandaloneInputClassName = React.useCallback(() => {
     const baseClasses = "w-20 px-3 py-1 text-sm text-center border rounded-md bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 transition-colors"
     
     if (!validationMessage) {
       return cn(baseClasses, "border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500")
     }
     
     switch (validationMessage.type) {
       case 'error':
         return cn(baseClasses, "border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 focus:ring-red-500 focus:border-red-500")
       case 'warning':
         return cn(baseClasses, "border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-300 focus:ring-amber-500 focus:border-amber-500")
       case 'info':
         return cn(baseClasses, "border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 focus:ring-blue-500 focus:border-blue-500")
       default:
         return cn(baseClasses, "border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500")
     }
   }, [validationMessage])

  return (
    <div className={cn("space-y-3", className)}>
      {/* Value display and steppers */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {showSteppers && showValue ? (
            // Grouped stepper with input - no gaps, cohesive design
             <div className={cn(
               "flex items-center border rounded-md bg-white dark:bg-gray-700 overflow-hidden transition-colors",
               validationMessage?.type === 'error' 
                 ? "border-red-300 dark:border-red-600" 
                 : validationMessage?.type === 'warning'
                 ? "border-amber-300 dark:border-amber-600"
                 : validationMessage?.type === 'info'
                 ? "border-blue-300 dark:border-blue-600"
                 : "border-gray-300 dark:border-gray-600"
             )}>
              <button
                type="button"
                onClick={() => handleStepperChange(-step)}
                disabled={isAtMin || disabled}
                className="w-8 h-8 flex items-center justify-center border-r border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Decrease value"
              >
                <ChevronDownIcon className="w-4 h-4" />
              </button>
              
              <input
                type="text"
                inputMode="numeric"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                disabled={disabled}
                className={getInputClassName()}
                aria-label="Slider value"
                aria-describedby={validationMessage ? "slider-validation" : undefined}
              />
              
              <button
                type="button"
                onClick={() => handleStepperChange(step)}
                disabled={isAtMax || disabled}
                className="w-8 h-8 flex items-center justify-center border-l border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Increase value"
              >
                <ChevronUpIcon className="w-4 h-4" />
              </button>
            </div>
          ) : showValue ? (
            // Just the input field without steppers
            <input
              type="text"
              inputMode="numeric"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              disabled={disabled}
              className={getStandaloneInputClassName()}
              aria-label="Slider value"
              aria-describedby={validationMessage ? "slider-validation" : undefined}
            />
          ) : showSteppers ? (
            // Just the stepper buttons without input
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleStepperChange(-step)}
                disabled={isAtMin || disabled}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Decrease value"
              >
                <ChevronDownIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleStepperChange(step)}
                disabled={isAtMax || disabled}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Increase value"
              >
                <ChevronUpIcon className="w-4 h-4" />
              </button>
            </div>
          ) : null}
        </div>
        
        {valueLabel && (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {valueLabel}
          </span>
        )}
      </div>
      
       {/* Validation message */}
       {showValidation && validationMessage && (
         <div 
           id="slider-validation"
           className={cn(
             "text-xs px-3 py-2 rounded-md border transition-all duration-200 mt-1",
             validationMessage.type === 'error' 
               ? "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800" 
               : validationMessage.type === 'warning'
               ? "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800"
               : "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800"
           )}
           role="alert"
           aria-live="polite"
         >
           <div className="flex items-center gap-2">
             <div className={cn(
               "w-1.5 h-1.5 rounded-full",
               validationMessage.type === 'error' 
                 ? "bg-red-500" 
                 : validationMessage.type === 'warning'
                 ? "bg-amber-500"
                 : "bg-blue-500"
             )} />
             <span className="font-medium">{validationMessage.message}</span>
           </div>
         </div>
       )}
      
      {/* Slider */}
      <SliderPrimitive.Root
        ref={ref}
        value={[currentValue]}
        onValueChange={handleValueChange}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className="relative flex w-full touch-none select-none items-center"
        {...props}
      >
        <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <SliderPrimitive.Range 
            className="absolute h-full bg-indigo-600" 
            style={{
              width: `${progressPercentage}%`
            }}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-indigo-600 bg-white shadow-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
      </SliderPrimitive.Root>
      
      {/* Min/Max labels */}
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  )
})

TremorSlider.displayName = "TremorSlider"

export { TremorSlider }
export type { TremorSliderProps }
